import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable, action, runInAction } from 'mobx';
import { Box, Dialog, Flex, FlexColumn, Text, Heading, Spinner, Tag, Button, Highcharts } from 'core/components';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

const GROUP_LABELS = {
  system_prompt: 'System Prompt',
  tool_definitions: 'Tool Definitions',
  tool_output: 'Tool Output',
  user_prompt: 'User Message',
  assistant_response: 'Assistant Response',
  history: 'History'
};

const GROUP_COLORS = {
  system_prompt: '#5C7CFA',
  tool_definitions: '#20C997',
  tool_output: '#FCC419',
  user_prompt: '#FF6B6B',
  assistant_response: '#845EF7',
  history: '#868E96'
};

const GROUP_ORDER = [
  'tool_output',
  'user_prompt',
  'assistant_response',
  'history',
  'system_prompt',
  'tool_definitions'
];

@inject('$auth')
@observer
class TokenUsageDialog extends Component {
  @observable.shallow analytics = [];

  @observable loading = true;

  @observable error = null;

  @observable selectedTurnId = null;

  @observable expandedGroups = new Set();

  @observable expandedSubAgents = new Set();

  @observable.ref actualUsage = null;

  @observable actualExpanded = false;

  componentDidMount() {
    this.fetchAnalytics();
  }

  @action
  fetchAnalytics() {
    const { sessionId } = this.props;
    this.loading = true;
    this.error = null;

    fetch(`/api/ui/kaizen/tokenAnalytics/${encodeURIComponent(sessionId)}`, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        runInAction(() => {
          // Response shape is { analytics, actualUsage }. Tolerate a bare array for
          // backward compatibility with older server builds.
          const analytics = Array.isArray(data) ? data : (data?.analytics ?? []);
          this.analytics = analytics;
          this.actualUsage = Array.isArray(data) ? null : (data?.actualUsage ?? null);
          this.loading = false;
          if (analytics.length > 0 && this.selectedTurnId == null) {
            this.selectedTurnId = analytics[0].agent_turn_id;
          }
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.error = err.message;
          this.loading = false;
        });
      });
  }

  @action
  selectTurn = (turnId) => {
    this.selectedTurnId = turnId;
  };

  @action
  toggleGroup = (turnId, componentType) => {
    const key = `${turnId}:${componentType}`;
    const next = new Set(this.expandedGroups);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedGroups = next;
  };

  @action
  expandAllGroups = (turnId, sortedRows) => {
    const next = new Set(this.expandedGroups);
    sortedRows.forEach((r) => next.add(`${turnId}:${r.component_type}`));
    this.expandedGroups = next;
  };

  @action
  collapseAllGroups = (turnId) => {
    const next = new Set(this.expandedGroups);
    for (const key of next) {
      if (key.startsWith(`${turnId}:`)) {
        next.delete(key);
      }
    }
    this.expandedGroups = next;
  };

  @action
  toggleSubAgent = (turnId, agentName) => {
    const key = `${turnId}:${agentName}`;
    const next = new Set(this.expandedSubAgents);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedSubAgents = next;
  };

  @action
  toggleActualExpanded = () => {
    this.actualExpanded = !this.actualExpanded;
  };

  getGroupedByTurn() {
    const turns = new Map();
    for (const row of this.analytics) {
      if (!turns.has(row.agent_turn_id)) {
        turns.set(row.agent_turn_id, {
          rows: [],
          contextWindowRows: [],
          subAgents: new Map(),
          total: 0,
          model: row.model,
          version: row.app_version
        });
      }
      const turn = turns.get(row.agent_turn_id);
      turn.rows.push(row);

      const agentName = row.details?.agent_name;
      if (agentName) {
        if (!turn.subAgents.has(agentName)) {
          turn.subAgents.set(agentName, {
            model: row.details?.model || 'unknown',
            rows: [],
            total: 0
          });
        }
        const sa = turn.subAgents.get(agentName);
        sa.rows.push(row);
        sa.total += row.estimated_tokens;
      } else {
        turn.contextWindowRows.push(row);
        turn.total += row.estimated_tokens;
        if (row.details?.model && !turn.modelFromDetails) {
          turn.model = row.details.model;
          turn.modelFromDetails = true;
        }
      }
    }
    return turns;
  }

  getActiveGroups(grouped) {
    const entries = [...grouped.entries()];
    return GROUP_ORDER.filter((group) =>
      entries.some(([, turnData]) =>
        turnData.contextWindowRows.some((r) => r.component_type === group && r.estimated_tokens > 0)
      )
    );
  }

  getStackedBarOptions(grouped) {
    const entries = [...grouped.entries()];
    const categories = entries.map((_, i) => `Turn ${i + 1}`);
    const activeGroups = this.getActiveGroups(grouped);

    const series = activeGroups.map((group) => ({
      name: GROUP_LABELS[group] || group,
      data: entries.map(([, turnData]) => {
        const row = turnData.contextWindowRows.find((r) => r.component_type === group);
        return row ? row.estimated_tokens : 0;
      })
    }));

    return {
      chart: { type: 'column' },
      title: { text: '' },
      xAxis: { categories, crosshair: true },
      yAxis: {
        title: { text: 'Tokens' },
        stackLabels: { enabled: true, format: '{total:,.0f}' }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          cursor: 'pointer',
          point: {
            events: {
              click: (e) => {
                const turnIndex = e.point.x;
                const turnId = entries[turnIndex][0];
                this.selectTurn(turnId);
              }
            }
          }
        }
      },
      tooltip: {
        shared: true,
        headerFormat: '<b>{point.key}</b><br/>',
        pointFormat: '\u25CF {series.name}: <b>{point.y:,.0f}</b><br/>',
        footerFormat: '<br/><b>Total: {point.total:,.0f}</b>'
      },
      legend: { enabled: true, itemStyle: { fontSize: '11px' } },
      series,
      credits: { enabled: false }
    };
  }

  getDonutOptions(turnData, turnIndex) {
    const data = GROUP_ORDER.map((group) => {
      const row = turnData.contextWindowRows.find((r) => r.component_type === group);
      if (!row || row.estimated_tokens <= 0) {
        return null;
      }
      if (group === 'history' && turnIndex === 0) {
        return null;
      }
      return { name: GROUP_LABELS[group] || group, y: row.estimated_tokens };
    })
      .filter(Boolean)
      .sort((a, b) => b.y - a.y);

    return {
      chart: { type: 'pie' },
      title: { text: '' },
      plotOptions: {
        pie: {
          innerSize: '55%',
          showInLegend: true,
          dataLabels: { enabled: false }
        }
      },
      legend: {
        enabled: true,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle',
        labelFormatter() {
          return `${this.name} <span style="opacity: 1">${this.y.toLocaleString()}</span>`;
        },
        useHTML: true
      },
      tooltip: { pointFormat: '<b>{point.y:,.0f}</b> tokens ({point.percentage:.1f}%)' },
      series: [{ name: 'Tokens', data }],
      credits: { enabled: false }
    };
  }

  getDonutColors(turnData, turnIndex) {
    return GROUP_ORDER.filter((group) => {
      const row = turnData.contextWindowRows.find((r) => r.component_type === group);
      if (!row || row.estimated_tokens <= 0) {
        return false;
      }
      if (group === 'history' && turnIndex === 0) {
        return false;
      }
      return true;
    })
      .map((group) => {
        const row = turnData.contextWindowRows.find((r) => r.component_type === group);
        return { group, tokens: row.estimated_tokens };
      })
      .sort((a, b) => b.tokens - a.tokens)
      .map(({ group }) => GROUP_COLORS[group]);
  }

  renderDetailsTable(row) {
    const { details, component_type } = row;
    if (!details) {
      return null;
    }

    if (component_type === 'system_prompt' && details.sections) {
      return details.sections
        .slice()
        .sort((a, b) => b.tokens - a.tokens)
        .map((s) => (
          <tr key={s.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '3px 8px 3px 24px' }}>
              <Text muted fontSize={12}>
                {s.name}
              </Text>
            </td>
            <td style={{ textAlign: 'right', padding: '3px 8px' }}>
              <Text fontSize={12}>{s.tokens.toLocaleString()}</Text>
            </td>
            <td />
          </tr>
        ));
    }

    if (component_type === 'tool_definitions' && details.tools) {
      return details.tools
        .slice()
        .sort((a, b) => b.tokens - a.tokens)
        .map((t) => (
          <tr key={t.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '3px 8px 3px 24px' }}>
              <Text muted fontSize={12}>
                {t.name}
              </Text>
            </td>
            <td style={{ textAlign: 'right', padding: '3px 8px' }}>
              <Text fontSize={12}>{t.tokens.toLocaleString()}</Text>
            </td>
            <td />
          </tr>
        ));
    }

    if (component_type === 'tool_output' && details.tools) {
      return details.tools
        .slice()
        .sort((a, b) => b.raw_tokens - a.raw_tokens)
        .map((t) => (
          <tr key={t.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '3px 8px 3px 24px' }}>
              <Text muted fontSize={12}>
                {t.name}
              </Text>
            </td>
            <td style={{ textAlign: 'right', padding: '3px 8px' }}>
              <Text fontSize={12}>{t.raw_tokens.toLocaleString()}</Text>
            </td>
            <td style={{ textAlign: 'right', padding: '3px 8px' }}>
              <Text muted fontSize={12}>
                {'\u2192'} {t.result_tokens.toLocaleString()} (
                {Math.round((t.result_tokens / Math.max(t.raw_tokens, 1)) * 100)}%)
              </Text>
            </td>
          </tr>
        ));
    }

    if (component_type === 'assistant_response' && details.messages) {
      return details.messages
        .slice()
        .sort((a, b) => b.tokens - a.tokens)
        .map((m) => (
          <tr key={m.type} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '3px 8px 3px 24px' }}>
              <Text muted fontSize={12}>
                {m.type}
              </Text>
            </td>
            <td style={{ textAlign: 'right', padding: '3px 8px' }}>
              <Text fontSize={12}>{m.tokens.toLocaleString()}</Text>
            </td>
            <td style={{ textAlign: 'right', padding: '3px 8px' }}>
              <Text muted fontSize={12}>
                {'\u00d7'}
                {m.count}
              </Text>
            </td>
          </tr>
        ));
    }

    return null;
  }

  renderTurnContent(turnId, turnData, turnIndex) {
    const sortedRows = [...turnData.contextWindowRows]
      .filter((row) => !(turnIndex === 0 && row.component_type === 'history'))
      .sort((a, b) => b.estimated_tokens - a.estimated_tokens);

    const allExpanded = sortedRows.every((r) => this.expandedGroups.has(`${turnId}:${r.component_type}`));

    return (
      <Box p={3}>
        <Box>
          <Highcharts
            key={`donut-${turnId}`}
            options={this.getDonutOptions(turnData, turnIndex)}
            colors={this.getDonutColors(turnData, turnIndex)}
            height={180}
          />
        </Box>

        <Flex justifyContent="flex-end" mb={1}>
          <Button
            minimal
            small
            icon={allExpanded ? 'collapse-all' : 'expand-all'}
            onClick={() => (allExpanded ? this.collapseAllGroups(turnId) : this.expandAllGroups(turnId, sortedRows))}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        </Flex>

        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Component</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Tokens</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Items</th>
              <th style={{ width: 28 }} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const groupKey = `${turnId}:${row.component_type}`;
              const isExpanded = this.expandedGroups.has(groupKey);
              const hasDetails =
                row.details &&
                ((row.component_type === 'system_prompt' && row.details.sections) ||
                  (row.component_type === 'tool_definitions' && row.details.tools) ||
                  (row.component_type === 'tool_output' && row.details.tools) ||
                  (row.component_type === 'assistant_response' && row.details.messages));
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={
                      [hasDetails ? 'token-row-clickable' : '', isExpanded ? 'token-row-expanded' : '']
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                    style={{
                      borderRadius: 4,
                      cursor: hasDetails ? 'pointer' : 'default',
                      transition: 'background 0.15s'
                    }}
                    onClick={() => hasDetails && this.toggleGroup(turnId, row.component_type)}
                  >
                    <td style={{ padding: '6px 8px' }}>
                      <Tag minimal style={{ borderLeft: `3px solid ${GROUP_COLORS[row.component_type]}` }}>
                        {GROUP_LABELS[row.component_type] || row.component_type}
                      </Tag>
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.estimated_tokens.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.item_count}</td>
                    <td style={{ textAlign: 'center', padding: '6px 4px', color: '#5C7CFA' }}>
                      {hasDetails && (isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />)}
                    </td>
                  </tr>
                  {isExpanded && this.renderDetailsTable(row)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {turnData.subAgents.size > 0 && this.renderSubAgents(turnId, turnData)}
      </Box>
    );
  }

  renderSubAgents(turnId, turnData) {
    return [...turnData.subAgents.entries()].map(([agentName, saData]) => {
      const saKey = `${turnId}:${agentName}`;
      const isExpanded = this.expandedSubAgents.has(saKey);
      const sortedSaRows = [...saData.rows].sort((a, b) => b.estimated_tokens - a.estimated_tokens);

      return (
        <Box key={agentName} mt={2}>
          <Flex
            alignItems="center"
            style={{ cursor: 'pointer' }}
            onClick={() => this.toggleSubAgent(turnId, agentName)}
          >
            {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
            <Tag minimal ml={1} style={{ borderLeft: '3px solid #845EF7' }}>
              {agentName}
            </Tag>
            <Text small muted ml={2}>
              ({saData.model})
            </Text>
            <Tag minimal small ml="auto">
              {saData.total.toLocaleString()} tokens
            </Tag>
          </Flex>
          {isExpanded && (
            <Box pl={3} mt={1}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>Component</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Tokens</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Items</th>
                    <th style={{ width: 28 }} />
                  </tr>
                </thead>
                <tbody>
                  {sortedSaRows.map((row) => {
                    const groupKey = `${saKey}:${row.component_type}`;
                    const isRowExpanded = this.expandedGroups.has(groupKey);
                    const hasDetails =
                      row.details &&
                      ((row.component_type === 'system_prompt' && row.details.sections) ||
                        (row.component_type === 'tool_definitions' && row.details.tools) ||
                        (row.component_type === 'tool_output' && row.details.tools) ||
                        (row.component_type === 'assistant_response' && row.details.messages));
                    return (
                      <React.Fragment key={`${row.id || row.component_type}`}>
                        <tr
                          className={
                            [hasDetails ? 'token-row-clickable' : '', isRowExpanded ? 'token-row-expanded' : '']
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                          style={{
                            borderRadius: 4,
                            cursor: hasDetails ? 'pointer' : 'default',
                            transition: 'background 0.15s'
                          }}
                          onClick={() => hasDetails && this.toggleGroup(saKey, row.component_type)}
                        >
                          <td style={{ padding: '6px 8px' }}>
                            <Tag
                              minimal
                              style={{ borderLeft: `3px solid ${GROUP_COLORS[row.component_type] || '#845EF7'}` }}
                            >
                              {GROUP_LABELS[row.component_type] || row.component_type}
                            </Tag>
                          </td>
                          <td style={{ textAlign: 'right', padding: '6px 8px' }}>
                            {row.estimated_tokens.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.item_count}</td>
                          <td style={{ textAlign: 'center', padding: '6px 4px', color: '#845EF7' }}>
                            {hasDetails && (isRowExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />)}
                          </td>
                        </tr>
                        {isRowExpanded && this.renderDetailsTable(row)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      );
    });
  }

  renderActualUsage() {
    const actual = this.actualUsage;
    if (!actual || !actual.total || actual.total.request_count === 0) {
      return null;
    }

    const { total, models } = actual;
    const isExpanded = this.actualExpanded;
    const fmt = (n) => Number(n || 0).toLocaleString();
    const fmtCost = (n) => `$${Number(n || 0).toFixed(4)}`;

    const columns = [
      { key: 'input_tokens', label: 'Input' },
      { key: 'output_tokens', label: 'Output' },
      { key: 'cached_tokens', label: 'Cached' },
      { key: 'reasoning_tokens', label: 'Reasoning' }
    ];

    return (
      <Box mb={2}>
        <Flex
          alignItems="center"
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          style={{ cursor: 'pointer' }}
          onClick={this.toggleActualExpanded}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.toggleActualExpanded();
            }
          }}
        >
          {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
          <Tag minimal ml={1} intent="success" style={{ borderLeft: '3px solid #2F9E44' }}>
            Actual Usage
          </Tag>
          <Text small muted ml={2}>
            {total.request_count} LLM {total.request_count === 1 ? 'request' : 'requests'}
          </Text>
          <Tag minimal small ml="auto">
            {fmtCost(total.total_cost)}
          </Tag>
        </Flex>
        {isExpanded && (
          <Box pl={3} mt={1}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Service</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Model</th>
                  {columns.map((c) => (
                    <th key={c.key} style={{ textAlign: 'right', padding: '4px 8px' }}>
                      {c.label}
                    </th>
                  ))}
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Cost</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Requests</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr
                    key={`${m.service_name}:${m.model_id}:${m.provider}`}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    <td style={{ padding: '6px 8px' }}>
                      <Text fontSize={12}>{m.service_name || '\u2014'}</Text>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <Text fontSize={12}>{m.model_id}</Text>{' '}
                      {m.provider && (
                        <Text muted fontSize={12}>
                          ({m.provider})
                        </Text>
                      )}
                    </td>
                    {columns.map((c) => (
                      <td key={c.key} style={{ textAlign: 'right', padding: '6px 8px' }}>
                        {fmt(m[c.key])}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtCost(m.total_cost)}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{m.request_count}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold' }}>
                  <td style={{ padding: '6px 8px' }}>Total</td>
                  <td />
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: 'right', padding: '6px 8px' }}>
                      {fmt(total[c.key])}
                    </td>
                  ))}
                  <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtCost(total.total_cost)}</td>
                  <td style={{ textAlign: 'right', padding: '6px 8px' }}>{total.request_count}</td>
                </tr>
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    );
  }

  renderUnifiedView(grouped, sessionTotal) {
    const entries = [...grouped.entries()];
    const selectedEntry = entries.find(([id]) => id === this.selectedTurnId) || entries[0];
    const selectedIndex = selectedEntry ? entries.indexOf(selectedEntry) : -1;

    const actual = this.actualUsage;
    const hasActual = !!(actual && actual.total && actual.total.request_count > 0);
    const actualTotalTokens = hasActual ? actual.total.input_tokens + actual.total.output_tokens : 0;

    return (
      <FlexColumn flex={1} style={{ minHeight: 0 }}>
        <Flex alignItems="center" gap={2} mb={1}>
          <Heading level={5} m={0}>
            Session Total
          </Heading>
          <Tag large intent="primary">
            {sessionTotal.toLocaleString()} estimated tokens
          </Tag>
          {hasActual && (
            <Tag large intent="success">
              {actualTotalTokens.toLocaleString()} actual tokens · ${actual.total.total_cost.toFixed(4)}
            </Tag>
          )}
        </Flex>

        {this.renderActualUsage()}

        <Box mb={1}>
          <Highcharts
            key="summary-chart"
            options={this.getStackedBarOptions(grouped)}
            colors={this.getActiveGroups(grouped).map((g) => GROUP_COLORS[g])}
            height={200}
          />
        </Box>

        <Flex flex={1} style={{ minHeight: 0, borderTop: '1px solid rgba(16, 22, 26, 0.15)' }}>
          <FlexColumn
            style={{
              width: 280,
              borderRight: '1px solid rgba(16, 22, 26, 0.15)',
              overflowY: 'auto'
            }}
          >
            {entries.map(([turnId, turnData], i) => {
              const isSelected = turnId === (selectedEntry && selectedEntry[0]);

              return (
                <Box
                  key={turnId}
                  className={`token-turn-row${isSelected ? ' token-turn-row-selected' : ''}`}
                  py={2}
                  px={2}
                  style={{
                    cursor: 'pointer',
                    borderLeft: `3px solid ${isSelected ? '#5C7CFA' : 'transparent'}`,
                    borderBottom: '1px solid rgba(16, 22, 26, 0.08)',
                    transition: 'background 0.15s'
                  }}
                  onClick={() => this.selectTurn(turnId)}
                >
                  <Flex alignItems="center" mb={1}>
                    <Text fontWeight="bold" mr={2}>
                      Turn {i + 1}
                    </Text>
                    <Tag minimal small round intent="primary">
                      {turnData.total.toLocaleString()} tokens
                    </Tag>
                  </Flex>
                  <Flex alignItems="center">
                    <Text small muted>
                      {turnData.model}
                      {turnData.subAgents.size > 0 && (
                        <>
                          {' '}
                          +{' '}
                          {[...turnData.subAgents.values()]
                            .map((sa) => sa.model)
                            .filter((v, idx, a) => a.indexOf(v) === idx)
                            .join(', ')}
                        </>
                      )}
                    </Text>
                  </Flex>
                </Box>
              );
            })}
          </FlexColumn>

          <Box flex={1} style={{ overflowY: 'auto' }}>
            {selectedEntry && this.renderTurnContent(selectedEntry[0], selectedEntry[1], selectedIndex)}
          </Box>
        </Flex>

        <style>{`
          .token-turn-row:hover {
            background: rgba(16, 22, 26, 0.05);
          }
          .token-turn-row-selected {
            background: rgba(92, 124, 250, 0.08);
          }
          .token-turn-row-selected:hover {
            background: rgba(92, 124, 250, 0.12);
          }
          .bp4-dark .token-turn-row:hover {
            background: rgba(138, 155, 168, 0.15);
          }
          .bp4-dark .token-turn-row-selected {
            background: rgba(92, 124, 250, 0.18);
          }
          .bp4-dark .token-turn-row-selected:hover {
            background: rgba(92, 124, 250, 0.25);
          }
          .token-row-clickable:hover {
            background: rgba(16, 22, 26, 0.05) !important;
          }
          .token-row-expanded {
            background: rgba(16, 22, 26, 0.06) !important;
          }
          .bp4-dark .token-row-clickable:hover {
            background: rgba(138, 155, 168, 0.15) !important;
          }
          .bp4-dark .token-row-expanded {
            background: rgba(138, 155, 168, 0.12) !important;
          }
        `}</style>
      </FlexColumn>
    );
  }

  render() {
    const { isOpen, onClose } = this.props;
    const grouped = this.getGroupedByTurn();
    const sessionTotal = this.analytics
      .filter((r) => !r.details?.agent_name)
      .reduce((sum, r) => sum + r.estimated_tokens, 0);

    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Token Usage"
        style={{ width: 1100, height: 'calc(100vh - 100px)' }}
      >
        <FlexColumn p={3} flex={1} style={{ minHeight: 0 }}>
          {this.loading && <Spinner />}
          {this.error && <Text color="danger">Failed to load analytics: {this.error}</Text>}
          {!this.loading && !this.error && this.analytics.length === 0 && (
            <Text muted>No token analytics available for this session.</Text>
          )}
          {!this.loading && !this.error && this.analytics.length > 0 && this.renderUnifiedView(grouped, sessionTotal)}
        </FlexColumn>
      </Dialog>
    );
  }
}

export default TokenUsageDialog;
